// Redux Utils
import { useDispatch, useSelector } from "react-redux";
import { updateInfoUserProfile } from "@/features/user/userThunks";

// React Utils
import { useState, useEffect } from "react";

// Paquetes
import { Country, State, City } from 'country-state-city';

export default function EditarInformacionPerfil() {
  const dispatch = useDispatch();
  const { info, isLoading, error } = useSelector((state) => state.user);
  
  // Estados para el formulario
  const [formData, setFormData] = useState({
    nombre: '',
    apellido: '',
    alias: '',
    numeroTributario: '',
    correo: '',
    telefono: '',
    fechaNacimiento: '',
    genero: '',
    facebook: '',
    instagram: '',
    tiktok: '',
    spotify: '',
    pais: '',
    ciudad: '',
    comuna: '',
  });
  
  // Estados para ubicación
  const [availableCountries, setAvailableCountries] = useState([]);
  const [availableStates, setAvailableStates] = useState([]);
  const [availableCities, setAvailableCities] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [selectedState, setSelectedState] = useState(null); // Representa la región/estado (se guarda en "ciudad")
  
  // Estados para validación y UI
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState(null);
  const [submitError, setSubmitError] = useState(null);
  const [rutError, setRutError] = useState('');

  // Cargar países al montar
  useEffect(() => {
    const allCountries = Country.getAllCountries();
    // Próximos: 'GT', 'MX', 'AR', 'PE', 'CO'
    const allowedCountryCodes = ['CL'];
    const countries = allCountries.filter(country => 
      allowedCountryCodes.includes(country.isoCode)
    );
    setAvailableCountries(countries);

    // Si ya hay un país en formData, configurarlo y cargar regiones
    if (formData.pais && countries.length > 0) {
      const country = countries.find(c => c.name === formData.pais);
      if (country) {
        setSelectedCountry(country);
        const states = State.getStatesOfCountry(country.isoCode);
        setAvailableStates(states);

        // Si también hay región/estado, configurarla y cargar ciudades
        if (formData.ciudad) {
          const state = states.find(s => s.name === formData.ciudad);
          if (state) {
            setSelectedState(state);
            const cities = City.getCitiesOfState(country.isoCode, state.isoCode);
            setAvailableCities(cities);
          }
        }
      }
    }
  }, [formData.pais, formData.ciudad]);

  // Cargar datos del usuario en el formulario
  useEffect(() => {
    if (info) {
      // Formatear fecha de nacimiento al formato YYYY-MM-DD para el input
      const fechaFormatted = info.fechaNacimiento 
        ? new Date(info.fechaNacimiento).toISOString().split('T')[0]
        : '';
      
      setFormData({
        nombre: info.nombre || '',
        apellido: info.apellido || '',
        alias: info.alias || '',
        numeroTributario: info.numeroTributario || '',
        correo: info.correo || '',
        telefono: info.telefono || '',
        fechaNacimiento: fechaFormatted,
        genero: info.genero || '',
        facebook: info.facebook || '',
        instagram: info.instagram || '',
        tiktok: info.tiktok || '',
        spotify: info.spotify || '',
        pais: info.pais || '',
        ciudad: info.ciudad || '', // En BD: ciudad = región/estado
        comuna: info.comuna || '', // En BD: comuna = ciudad específica
      });
    }
  }, [info]);

  // Validar RUT chileno
  const validarRUT = (rut) => {
    // Limpiar el RUT de puntos y guiones
    const rutLimpio = rut.replace(/\./g, '').replace(/-/g, '');
    
    if (rutLimpio.length < 8 || rutLimpio.length > 10) {
      return false;
    }
    
    // Separar el cuerpo del dígito verificador
    const cuerpo = rutLimpio.slice(0, -1);
    const dv = rutLimpio.slice(-1).toUpperCase();
    
    // Calcular el dígito verificador
    let suma = 0;
    let multiplicador = 2;
    
    for (let i = cuerpo.length - 1; i >= 0; i--) {
      suma += parseInt(cuerpo.charAt(i)) * multiplicador;
      multiplicador = multiplicador === 7 ? 2 : multiplicador + 1;
    }
    
    const dvEsperado = 11 - (suma % 11);
    const dvCalculado = dvEsperado === 11 ? '0' : dvEsperado === 10 ? 'K' : dvEsperado.toString();
    
    return dv === dvCalculado;
  };

  // Formatear RUT mientras se escribe
  const formatearRUT = (rut) => {
    // Limpiar el RUT
    const rutLimpio = rut.replace(/\./g, '').replace(/-/g, '').toUpperCase();
    
    if (rutLimpio.length <= 1) return rutLimpio;
    
    // Separar cuerpo y dígito verificador
    const cuerpo = rutLimpio.slice(0, -1);
    const dv = rutLimpio.slice(-1);
    
    // Formatear cuerpo con puntos
    let cuerpoFormateado = '';
    let contador = 0;
    
    for (let i = cuerpo.length - 1; i >= 0; i--) {
      if (contador === 3) {
        cuerpoFormateado = '.' + cuerpoFormateado;
        contador = 0;
      }
      cuerpoFormateado = cuerpo.charAt(i) + cuerpoFormateado;
      contador++;
    }
    
    return `${cuerpoFormateado}-${dv}`;
  };

  // Manejar cambios en los inputs
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Validación especial para RUT
    if (name === 'numeroTributario') {
      // Permitir solo números, K y caracteres de formato
      const rutLimpio = value.replace(/[^0-9Kk]/g, '');
      
      if (rutLimpio.length <= 10) {
        const rutFormateado = formatearRUT(rutLimpio);
        setFormData(prev => ({ ...prev, [name]: rutFormateado }));
        
        // Validar RUT si tiene longitud suficiente
        if (rutLimpio.length >= 8) {
          if (validarRUT(rutFormateado)) {
            setRutError('');
          } else {
            setRutError('RUT inválido');
          }
        } else {
          setRutError('');
        }
      }
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
    
    // Limpiar mensajes al editar
    setSubmitMessage(null);
    setSubmitError(null);
  };

  // Manejar cambio de país
  const handleCountryChange = (e) => {
    const countryCode = e.target.value;
    const country = availableCountries.find(c => c.isoCode === countryCode);
    
    setSelectedCountry(country);
    setFormData(prev => ({ 
      ...prev, 
      pais: country ? country.name : '',
      ciudad: '', // Resetear región/estado
      comuna: ''  // Resetear ciudad/comuna
    }));
    
    if (country) {
      const states = State.getStatesOfCountry(country.isoCode);
      setAvailableStates(states);
    } else {
      setAvailableStates([]);
    }
    
    setAvailableCities([]);
    setSelectedState(null);
  };

  // Manejar cambio de región/estado (se guarda en campo "ciudad")
  const handleStateChange = (e) => {
    const stateCode = e.target.value;
    const state = availableStates.find(s => s.isoCode === stateCode);
    
    setSelectedState(state);
    setFormData(prev => ({ 
      ...prev, 
      ciudad: state ? state.name : '', // Guardar nombre de región/estado en "ciudad"
      comuna: '' // Resetear ciudad/comuna
    }));
    
    if (state && selectedCountry) {
      const cities = City.getCitiesOfState(selectedCountry.isoCode, state.isoCode);
      setAvailableCities(cities);
    } else {
      setAvailableCities([]);
    }
  };

  // Manejar cambio de ciudad/comuna (se guarda en campo "comuna")
  const handleCityChange = (e) => {
    const cityName = e.target.value;
    setFormData(prev => ({ ...prev, comuna: cityName })); // Guardar en "comuna"
  };

  // Validar formulario
  const validarFormulario = () => {
    const errors = [];
    
    // Validar campos obligatorios
    if (!formData.nombre.trim()) errors.push('El nombre es obligatorio');
    if (!formData.apellido.trim()) errors.push('El apellido es obligatorio');
    if (!formData.numeroTributario.trim()) errors.push('El RUT es obligatorio');
    if (!formData.correo.trim()) errors.push('El correo es obligatorio');
    if (!formData.fechaNacimiento) errors.push('La fecha de nacimiento es obligatoria');
    if (!formData.genero) errors.push('El género es obligatorio');
    if (!formData.pais) errors.push('El país es obligatorio');
    if (!formData.ciudad.trim()) errors.push('La región/estado es obligatoria');
    // comuna es opcional
    
    // Validar RUT
    if (formData.numeroTributario && !validarRUT(formData.numeroTributario)) {
      errors.push('El RUT ingresado no es válido');
    }
    
    // Validar formato de correo
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (formData.correo && !emailRegex.test(formData.correo)) {
      errors.push('El formato del correo no es válido');
    }
    
    return errors;
  };

  // Manejar envío del formulario
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validar formulario
    const errors = validarFormulario();
    if (errors.length > 0) {
      setSubmitError(errors.join('. '));
      setTimeout(() => setSubmitError(null), 5000);
      return;
    }
    
    setIsSubmitting(true);
    setSubmitMessage(null);
    setSubmitError(null);
    
    try {
      await dispatch(updateInfoUserProfile(formData)).unwrap();
      setSubmitMessage('✓ Información actualizada correctamente');
      setTimeout(() => setSubmitMessage(null), 5000);
    } catch (error) {
      setSubmitError(error || 'Error al actualizar la información');
      setTimeout(() => setSubmitError(null), 5000);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-aw-bluedark-95 rounded-lg p-6 min-h-[400px]">
      <div className="max-w-4xl mx-auto">
        <div className="mb-6">
          <h3 className="text-2xl font-semibold text-white mb-2">
            Información del Perfil
          </h3>
          <p className="text-gray-400 text-sm">
            Mientras más completa esté tu información, mejor será tu experiencia en la plataforma
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Sección: Datos Personales */}
          <div className="bg-aw-bluedark rounded-lg p-6">
            <h4 className="text-xl font-semibold text-white mb-4 pb-2 border-b border-gray-700">
              Datos Personales
            </h4>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Nombre */}
              <div>
                <label htmlFor="nombre" className="block text-sm font-medium text-gray-300 mb-2">
                  Nombre <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="nombre"
                  name="nombre"
                  value={formData.nombre}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 bg-aw-blueblack border border-gray-600 rounded-lg text-white focus:outline-none focus:border-aw-green"
                  required
                />
              </div>

              {/* Apellido */}
              <div>
                <label htmlFor="apellido" className="block text-sm font-medium text-gray-300 mb-2">
                  Apellido <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="apellido"
                  name="apellido"
                  value={formData.apellido}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 bg-aw-blueblack border border-gray-600 rounded-lg text-white focus:outline-none focus:border-aw-green"
                  required
                />
              </div>

              {/* Alias */}
              <div>
                <label htmlFor="alias" className="block text-sm font-medium text-gray-300 mb-2">
                  Alias
                </label>
                <input
                  type="text"
                  id="alias"
                  name="alias"
                  value={formData.alias}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 bg-aw-blueblack border border-gray-600 rounded-lg text-white focus:outline-none focus:border-aw-green"
                  placeholder="Opcional"
                />
              </div>

              {/* RUT */}
              <div>
                <label htmlFor="numeroTributario" className="block text-sm font-medium text-gray-300 mb-2">
                  RUT <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="numeroTributario"
                  name="numeroTributario"
                  value={formData.numeroTributario}
                  onChange={handleInputChange}
                  className={`w-full px-4 py-2 bg-aw-blueblack border ${
                    rutError ? 'border-red-500' : 'border-gray-600'
                  } rounded-lg text-white focus:outline-none focus:border-aw-green`}
                  placeholder="12.345.678-9"
                  required
                />
                {rutError && (
                  <p className="text-red-500 text-xs mt-1">{rutError}</p>
                )}
              </div>

              {/* Correo */}
              <div>
                <label htmlFor="correo" className="block text-sm font-medium text-gray-300 mb-2">
                  Correo Electrónico <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  id="correo"
                  name="correo"
                  value={formData.correo}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 bg-aw-blueblack border border-gray-600 rounded-lg text-white focus:outline-none focus:border-aw-green"
                  required
                />
              </div>

              {/* Teléfono */}
              <div>
                <label htmlFor="telefono" className="block text-sm font-medium text-gray-300 mb-2">
                  WhatsApp
                </label>
                <span className="text-gray-400 text-xs mb-2 block">Al llenar este campo, aceptas inmediatamente que se muestre tu número de WhatsApp en tu perfil público</span>
                <input
                  type="tel"
                  id="telefono"
                  name="telefono"
                  value={formData.telefono}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 bg-aw-blueblack border border-gray-600 rounded-lg text-white focus:outline-none focus:border-aw-green"
                  placeholder="Opcional"
                />
              </div>

              {/* Fecha de Nacimiento */}
              <div>
                <label htmlFor="fechaNacimiento" className="block text-sm font-medium text-gray-300 mb-2">
                  Fecha de Nacimiento <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  id="fechaNacimiento"
                  name="fechaNacimiento"
                  value={formData.fechaNacimiento}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 bg-aw-blueblack border border-gray-600 rounded-lg text-white focus:outline-none focus:border-aw-green"
                  required
                />
              </div>

              {/* Género */}
              <div>
                <label htmlFor="genero" className="block text-sm font-medium text-gray-300 mb-2">
                  Género <span className="text-red-500">*</span>
                </label>
                <select
                  id="genero"
                  name="genero"
                  value={formData.genero}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 bg-aw-blueblack border border-gray-600 rounded-lg text-white focus:outline-none focus:border-aw-green"
                  required
                >
                  <option value="">Selecciona tu género</option>
                  <option value="mujer">Soy Mujer</option>
                  <option value="hombre">Soy Hombre</option>
                  <option value="otro">Otro</option>
                </select>
              </div>
            </div>
          </div>

          {/* Sección: Redes Sociales */}
          <div className="bg-aw-bluedark rounded-lg p-6">
            <h4 className="text-xl font-semibold text-white mb-4 pb-2 border-b border-gray-700">
              Redes Sociales
            </h4>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Facebook */}
              <div>
                <label htmlFor="facebook" className="block text-sm font-medium text-gray-300 mb-2">
                  <i className="fab fa-facebook mr-2"></i>Facebook
                </label>
                <input
                  type="url"
                  id="facebook"
                  name="facebook"
                  value={formData.facebook}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 bg-aw-blueblack border border-gray-600 rounded-lg text-white focus:outline-none focus:border-aw-green"
                  placeholder="https://facebook.com/tu-perfil"
                />
              </div>

              {/* Instagram */}
              <div>
                <label htmlFor="instagram" className="block text-sm font-medium text-gray-300 mb-2">
                  <i className="fab fa-instagram mr-2"></i>Instagram
                </label>
                <input
                  type="url"
                  id="instagram"
                  name="instagram"
                  value={formData.instagram}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 bg-aw-blueblack border border-gray-600 rounded-lg text-white focus:outline-none focus:border-aw-green"
                  placeholder="https://instagram.com/tu-perfil"
                />
              </div>

              {/* TikTok */}
              <div>
                <label htmlFor="tiktok" className="block text-sm font-medium text-gray-300 mb-2">
                  <i className="fab fa-tiktok mr-2"></i>TikTok
                </label>
                <input
                  type="url"
                  id="tiktok"
                  name="tiktok"
                  value={formData.tiktok}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 bg-aw-blueblack border border-gray-600 rounded-lg text-white focus:outline-none focus:border-aw-green"
                  placeholder="https://tiktok.com/@tu-perfil"
                />
              </div>

              {/* Spotify */}
              <div>
                <label htmlFor="spotify" className="block text-sm font-medium text-gray-300 mb-2">
                  <i className="fab fa-spotify mr-2"></i>Spotify
                </label>
                <input
                  type="url"
                  id="spotify"
                  name="spotify"
                  value={formData.spotify}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 bg-aw-blueblack border border-gray-600 rounded-lg text-white focus:outline-none focus:border-aw-green"
                  placeholder="https://open.spotify.com/user/tu-perfil"
                />
              </div>
            </div>
          </div>

          {/* Sección: Ubicación */}
          <div className="bg-aw-bluedark rounded-lg p-6">
            <h4 className="text-xl font-semibold text-white mb-4 pb-2 border-b border-gray-700">
              Ubicación
            </h4>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* País */}
              <div>
                <label htmlFor="pais" className="block text-sm font-medium text-gray-300 mb-2">
                  País <span className="text-red-500">*</span>
                </label>
                <select
                  id="pais"
                  name="pais"
                  value={selectedCountry?.isoCode || ''}
                  onChange={handleCountryChange}
                  className="w-full px-4 py-2 bg-aw-blueblack border border-gray-600 rounded-lg text-white focus:outline-none focus:border-aw-green"
                  required
                >
                  <option value="">Selecciona un país</option>
                  {availableCountries.map((country) => (
                    <option key={country.isoCode} value={country.isoCode}>
                      {country.flag} {country.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Región/Estado (se guarda en campo "ciudad") */}
              <div>
                <label htmlFor="region" className="block text-sm font-medium text-gray-300 mb-2">
                  Región/Estado <span className="text-red-500">*</span>
                </label>
                <select
                  id="region"
                  name="region"
                  value={selectedState?.isoCode || ''}
                  onChange={handleStateChange}
                  onFocus={(e) => {
                    if (!selectedCountry) {
                      e.target.blur();
                      alert('Por favor, selecciona un país primero');
                    }
                  }}
                  className="w-full px-4 py-2 bg-aw-blueblack border border-gray-600 rounded-lg text-white focus:outline-none focus:border-aw-green"
                  disabled={!selectedCountry}
                  required
                >
                  <option value="">Selecciona una región</option>
                  {availableStates.map((state) => (
                    <option key={state.isoCode} value={state.isoCode}>
                      {state.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Ciudad/Comuna (se guarda en campo "comuna") */}
              <div>
                <label htmlFor="comuna" className="block text-sm font-medium text-gray-300 mb-2">
                  Ciudad/Comuna
                </label>
                {availableCities.length > 0 ? (
                  <select
                    id="comuna"
                    name="comuna"
                    value={formData.comuna}
                    onChange={handleCityChange}
                    className="w-full px-4 py-2 bg-aw-blueblack border border-gray-600 rounded-lg text-white focus:outline-none focus:border-aw-green"
                  >
                    <option value="">Selecciona una ciudad</option>
                    {availableCities.map((city) => (
                      <option key={city.name} value={city.name}>
                        {city.name}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    type="text"
                    id="comuna"
                    name="comuna"
                    value={formData.comuna}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 bg-aw-blueblack border border-gray-600 rounded-lg text-white focus:outline-none focus:border-aw-green"
                    placeholder="Opcional"
                    disabled={!selectedState}
                  />
                )}
              </div>
            </div>
          </div>

          {/* Mensajes de estado */}
          {submitMessage && (
            <div className="bg-green-500/20 border border-green-500 rounded-lg p-4">
              <p className="text-green-400 text-center font-medium">{submitMessage}</p>
            </div>
          )}

          {submitError && (
            <div className="bg-red-500/20 border border-red-500 rounded-lg p-4">
              <p className="text-red-400 text-center font-medium">{submitError}</p>
            </div>
          )}

          {/* Botón de envío */}
          <div className="flex justify-center">
            <button
              type="submit"
              disabled={isSubmitting || rutError}
              className={`px-8 py-3 rounded-lg font-semibold text-lg transition-all ${
                isSubmitting || rutError
                  ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                  : 'bg-aw-green text-aw-bluedark hover:scale-105'
              }`}
            >
              {isSubmitting ? (
                <>
                  <i className="fas fa-spinner fa-spin mr-2"></i>
                  Actualizando...
                </>
              ) : (
                <>
                  <i className="fas fa-save mr-2"></i>
                  Actualizar Información
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
